// Frontend/geniac/src/Components/Settings/GDPRPage.tsx
// Page de gestion RGPD (export et suppression de données)

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exportUserData, requestAccountDeletion } from '../../api/gdpr';
import './GDPRPage.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  type?: 'danger' | 'warning';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  type = 'warning'
}) => {
  if (!isOpen) return null;

  return (
    <div className="gdpr-modal-overlay" onClick={onClose}>
      <div className="gdpr-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`gdpr-modal-header ${type}`}>
          <h3>{title}</h3>
        </div>
        <div className="gdpr-modal-body">
          <p>{message}</p>
        </div>
        <div className="gdpr-modal-footer">
          <button
            className="gdpr-btn gdpr-btn-secondary"
            onClick={onClose}
          >
            {cancelText}
          </button>
          <button
            className={`gdpr-btn gdpr-btn-${type}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const GDPRPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteRequestSent, setDeleteRequestSent] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Export des données
  const handleExportData = async () => {
    setIsExporting(true);
    setExportSuccess(false);
    setExportError(null);

    try {
      const blob = await exportUserData();

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `geniac-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 5000);
    } catch (error: any) {
      console.error('Export error:', error);
      setExportError(
        error.response?.data?.message ||
        t('gdpr.exportError')
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Demande de suppression de compte
  const handleRequestDeletion = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await requestAccountDeletion();

      if (response.confirmation_email_sent) {
        setDeleteRequestSent(true);
        setShowDeleteModal(false);
      }
    } catch (error: any) {
      console.error('Deletion request error:', error);
      setDeleteError(
        error.response?.data?.message ||
        t('gdpr.deletionError')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="gdpr-page">
      <div className="gdpr-header">
        <h2>{t('gdpr.title')}</h2>
        <p className="gdpr-subtitle">{t('gdpr.subtitle')}</p>
      </div>

      {/* Section Export de données */}
      <div className="gdpr-section">
        <div className="gdpr-section-header">
          <h3>
            <span className="gdpr-icon">📦</span>
            {t('gdpr.exportTitle')}
          </h3>
          <span className="gdpr-badge">Article 20 RGPD</span>
        </div>

        <p className="gdpr-description">
          {t('gdpr.exportDescription')}
        </p>

        <div className="gdpr-content">
          <ul className="gdpr-data-list">
            <li>{t('gdpr.dataIncluded.personalInfo')}</li>
            <li>{t('gdpr.dataIncluded.organization')}</li>
            <li>{t('gdpr.dataIncluded.subscriptions')}</li>
            <li>{t('gdpr.dataIncluded.projects')}</li>
            <li>{t('gdpr.dataIncluded.architectures')}</li>
            <li>{t('gdpr.dataIncluded.deployments')}</li>
            <li>{t('gdpr.dataIncluded.invoices')}</li>
            <li>{t('gdpr.dataIncluded.usage')}</li>
          </ul>

          {exportSuccess && (
            <div className="gdpr-alert gdpr-alert-success">
              <span className="alert-icon">✓</span>
              {t('gdpr.exportSuccess')}
            </div>
          )}

          {exportError && (
            <div className="gdpr-alert gdpr-alert-error">
              <span className="alert-icon">✕</span>
              {exportError}
            </div>
          )}

          <button
            className="gdpr-btn gdpr-btn-primary"
            onClick={handleExportData}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <span className="spinner"></span>
                {t('gdpr.exporting')}
              </>
            ) : (
              <>
                <span>📥</span>
                {t('gdpr.exportButton')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Section Suppression de compte */}
      <div className="gdpr-section gdpr-section-danger">
        <div className="gdpr-section-header">
          <h3>
            <span className="gdpr-icon">🗑️</span>
            {t('gdpr.deleteTitle')}
          </h3>
          <span className="gdpr-badge gdpr-badge-danger">Article 17 RGPD</span>
        </div>

        <p className="gdpr-description">
          {t('gdpr.deleteDescription')}
        </p>

        <div className="gdpr-content">
          <div className="gdpr-warning">
            <span className="warning-icon">⚠️</span>
            <div>
              <strong>{t('gdpr.deleteWarningTitle')}</strong>
              <p>{t('gdpr.deleteWarning')}</p>
            </div>
          </div>

          <ul className="gdpr-deletion-list">
            <li>{t('gdpr.deletionEffects.projects')}</li>
            <li>{t('gdpr.deletionEffects.architectures')}</li>
            <li>{t('gdpr.deletionEffects.subscriptions')}</li>
            <li>{t('gdpr.deletionEffects.invoices')}</li>
          </ul>

          {deleteRequestSent && (
            <div className="gdpr-alert gdpr-alert-info">
              <span className="alert-icon">📧</span>
              {t('gdpr.deleteEmailSent')}
            </div>
          )}

          {deleteError && (
            <div className="gdpr-alert gdpr-alert-error">
              <span className="alert-icon">✕</span>
              {deleteError}
            </div>
          )}

          <button
            className="gdpr-btn gdpr-btn-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={isDeleting || deleteRequestSent}
          >
            {isDeleting ? (
              <>
                <span className="spinner"></span>
                {t('gdpr.processing')}
              </>
            ) : (
              <>
                <span>🗑️</span>
                {t('gdpr.deleteButton')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal de confirmation */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleRequestDeletion}
        title={t('gdpr.confirmDeleteTitle')}
        message={t('gdpr.confirmDeleteMessage')}
        confirmText={t('gdpr.confirmDelete')}
        cancelText={t('gdpr.cancel')}
        type="danger"
      />
    </div>
  );
};

export default GDPRPage;
